import { Icons } from '@/assets/icons/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { providerIcon } from '@/features/action-cards/icons';
import { cn } from '@/lib/utils';

import { useCardSaveEdits, type CardOpen } from '../cardOpen';

const PILL_CLASS =
  'relative z-[1] inline-flex h-[calc(22*var(--ac-u))] shrink-0 items-center gap-[calc(4*var(--ac-u))] whitespace-nowrap rounded-full border border-border bg-surface pl-[calc(6*var(--ac-u))] pr-[calc(8*var(--ac-u))] text-[length:calc(11.5*var(--ac-u))] font-medium leading-none text-foreground focus-ring transition-colors duration-[120ms] hover:bg-surface-hover';

const ARROW_CLASS =
  'relative z-[1] flex size-[calc(22*var(--ac-u))] shrink-0 items-center justify-center rounded-full bg-secondary text-foreground-subtle focus-ring hover:bg-secondary-hover hover:text-foreground';

type FeedOpenLinkProps = {
  variant?: 'pill' | 'arrow';
  className?: string;
  url: string;
  appName: string;
  onOpenUrl: CardOpen;
};

const GLYPH_BY_APP = new Map(
  Object.entries(providerIcon).map(([id, glyph]) => [id.toLowerCase(), glyph])
);

export const appGlyphOf = (appName: string) =>
  GLYPH_BY_APP.get(appName.toLowerCase().replace(/\s+/g, '-'));

export const FeedOpenLink = ({
  variant = 'pill',
  className,
  url,
  appName,
  onOpenUrl,
}: FeedOpenLinkProps) => {
  // The open card's header sits beside the card, so its open awaits the card's save through this
  const saveEdits = useCardSaveEdits();
  const AppGlyph = appGlyphOf(appName);
  const label = `Open in ${appName}`;
  const arrow = variant === 'arrow';

  const link = (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={arrow ? label : undefined}
      className={cn(arrow ? ARROW_CLASS : PILL_CLASS, className)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenUrl(url, saveEdits);
      }}
    >
      {arrow ? (
        <Icons.arrowUpRight
          aria-hidden="true"
          className="size-[calc(13*var(--ac-u))]"
        />
      ) : (
        <>
          {AppGlyph ? (
            <AppGlyph className="size-[calc(12*var(--ac-u))] shrink-0" />
          ) : null}
          {label}
          <Icons.arrowUpRight
            className="size-[calc(10*var(--ac-u))] shrink-0 text-foreground-muted"
            aria-hidden="true"
          />
        </>
      )}
    </a>
  );
  return arrow ? <Tooltip label={label}>{link}</Tooltip> : link;
};
